// src/context/ThemeContext.js
import React, { createContext, useState, useEffect, useMemo, useCallback } from 'react';
import { ThemeProvider as StyledThemeProvider } from 'styled-components';
import theme from '../styles/theme';
import GlobalStyles from '../styles/globalStyles';
import { getThemeById } from '../styles/colorThemes';

export const ThemeContext = createContext();

export const ThemeProvider = ({ children }) => {
  // Dark mode is always enabled, light mode is disabled
  const [darkMode, setDarkMode] = useState(true);
  const [gradientEnabled, setGradientEnabled] = useState(true);
  const [selectedColorTheme, setSelectedColorTheme] = useState('cyanTeal');
  const [currentTheme, setCurrentTheme] = useState(theme);

  useEffect(() => {
    // Dark mode is always enabled, ignore any saved preferences
    // Only load gradient preference and color theme
    const savedGradientEnabled = localStorage.getItem('gradientEnabled') !== 'false'; // Default to true
    const savedColorTheme = localStorage.getItem('selectedColorTheme') || 'cyanTeal';
    setDarkMode(true); // Always set to dark mode
    setGradientEnabled(savedGradientEnabled);
    setSelectedColorTheme(savedColorTheme);
  }, []);

  useEffect(() => {
    // Save user preferences (dark mode is always true, so we don't save it)
    localStorage.setItem('darkMode', 'true'); // Always save as true
    localStorage.setItem('gradientEnabled', gradientEnabled);
    localStorage.setItem('selectedColorTheme', selectedColorTheme);
    
    // Get the selected color theme - always use dark mode
    const mode = 'dark'; // Always dark mode
    const colorTheme = getThemeById(selectedColorTheme, mode);
    
    // Update theme based on dark mode (always dark) and color theme
    // Deep merge color theme with base theme for comprehensive coverage
    // Dark mode is always enabled, so we only set dark theme
    setCurrentTheme({
        ...theme,
        name: 'dark',
        gradientEnabled,
        colorTheme: colorTheme,
        colors: {
          ...theme.colors,
          // Primary colors from selected theme
          primary: colorTheme.primary.main,
          primaryLight: colorTheme.primary.light,
          primaryDark: colorTheme.primary.dark,
          primaryGradient: colorTheme.primary.gradient,
          primaryGlow: colorTheme.primary.glow,
          // Secondary colors
          secondary: colorTheme.secondary.main,
          secondaryLight: colorTheme.secondary.light,
          secondaryDark: colorTheme.secondary.dark,
          secondaryGradient: colorTheme.secondary.gradient,
          secondaryGlow: colorTheme.secondary.glow,
          // Success colors
          success: colorTheme.success.main,
          successLight: colorTheme.success.light,
          successDark: colorTheme.success.dark,
          successGradient: colorTheme.success.gradient,
          successGlow: colorTheme.success.glow,
          // Warning colors
          warning: colorTheme.warning.main,
          warningLight: colorTheme.warning.light,
          warningDark: colorTheme.warning.dark,
          warningGradient: colorTheme.warning.gradient,
          warningGlow: colorTheme.warning.glow,
          // Danger/Error colors
          error: colorTheme.danger.main,
          danger: colorTheme.danger.main,
          dangerLight: colorTheme.danger.light,
          dangerDark: colorTheme.danger.dark,
          dangerGradient: colorTheme.danger.gradient,
          dangerGlow: colorTheme.danger.glow,
          // Emergency colors
          emergency: colorTheme.emergency.main,
          emergencyLight: colorTheme.emergency.light,
          emergencyDark: colorTheme.emergency.dark,
          emergencyGradient: colorTheme.emergency.gradient,
          emergencyGlow: colorTheme.emergency.glow,
          // Background colors
          background: {
            main: '#121212',
            paper: '#1E1E1E',
            dark: '#0A0A0A'
          },
          // Text colors from theme
          text: {
            primary: colorTheme.text?.primary || '#FFFFFF',
            secondary: colorTheme.text?.secondary || '#B0BEC5',
            disabled: '#666666',
            hint: '#666666',
            muted: colorTheme.text?.muted || '#9E9E9E'
          }
        }
      });
    // Light mode is disabled - removed else block
  }, [darkMode, gradientEnabled, selectedColorTheme]);

  // Dark mode toggle is disabled - always stays in dark mode
  const toggleDarkMode = useCallback(() => {
    // No-op: dark mode is always enabled, light mode is disabled
    setDarkMode(true);
  }, []);

  const toggleGradient = useCallback(() => {
    setGradientEnabled(prev => !prev);
  }, []);

  const colorTheme = useMemo(() => getThemeById(selectedColorTheme, 'dark'), [selectedColorTheme]);

  const value = useMemo(() => ({
    theme: currentTheme,
    darkMode,
    gradientEnabled,
    selectedColorTheme,
    setSelectedColorTheme,
    toggleDarkMode,
    toggleGradient,
    colorTheme
  }), [currentTheme, darkMode, gradientEnabled, selectedColorTheme, toggleDarkMode, toggleGradient, colorTheme]);

  return (
    <ThemeContext.Provider value={value}>
      <StyledThemeProvider theme={currentTheme}>
        <GlobalStyles />
        {children}
      </StyledThemeProvider>
    </ThemeContext.Provider>
  );
};

export default ThemeProvider;